/**
 * TypingPresenceRows — who else in this room is typing, drawn as rows in the ONE
 * overlay lane (T-R15, Miles 2026-09-21: *"there should also be a functionality to
 * see when someone is typing it should show a list like this in there consistently
 * when human users are typing as well. when there's no changes to the draft box in
 * 4 seconds it should not say [user name] is responding"*).
 *
 * A LIST, like the live-turn cards above it, because that is the surface he pointed
 * at — one row per person, their glyph and their name, capped with a `+N more` tail.
 * It is deliberately NOT a transcript row: T-G3 (the "new messages" rule) governs
 * the transcript, and presence has no place in history.
 *
 * Three properties, each load-bearing and each pinned:
 *
 *  1. NEVER THE DRAFT. A typing doc carries a uid, a server stamp and a schema
 *     version. Nothing here can render what someone wrote, or how much, because
 *     nothing about it was ever published.
 *  2. NEVER A ROLE (R8). A row is a NAME and a glyph, resolved through the SAME
 *     roster chain the sender chip, the reply quote and the live-turn card header
 *     read, so the four can never disagree about a person's name.
 *  3. NEVER THIS TAB'S WORK. It lights no processing flag, takes no streaming
 *     lease and dispatches nothing — a colleague's keystroke must not spin the
 *     room's pending widget (streamingFlag.ts, the greyed-out-refresh class).
 *
 * Its own clock: a row must DISAPPEAR when its writer's tab dies mid-draft, and the
 * staleness belt that decides this is a function of time. So this component ticks
 * itself (the LiveTurnCardsOverlay idiom) and only while it has rows — re-rendering
 * the transcript once a second to expire a presence row would be the wrong price.
 */
import React, { useEffect, useState } from 'react';
import {
  composeTypingRows,
  type TypingRow,
  type TypingView,
} from '../../services/groupThreads/typingPresence';

/** The source tag every row wears — the D9 registry entry's key. */
export const TYPING_PRESENCE_SOURCE_TAG = 'typing_presence';

export const TYPING_ROW_TICK_MS = 1_000;

/** A person, as the room draws them: a name and a glyph, never a role (R8). */
export interface TypingRowLabel {
  name: string;
  initial: string;
}

export interface TypingPresenceRowsProps {
  /** The store's rows, unfiltered — the viewer's own is dropped here. */
  docs: ReadonlyArray<TypingView>;
  /** The person looking. */
  viewerUid: string | null;
  /** The room's roster, when known: nobody outside it is ever drawn. */
  participantUids?: ReadonlyArray<string> | null;
  /** The host resolves a NAME and a glyph — never a role (R8). */
  labelFor: (uid: string) => TypingRowLabel;
}

function useTypingClock(active: boolean): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), TYPING_ROW_TICK_MS);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

export function TypingPresenceRows(
  { docs, viewerUid, participantUids, labelFor }: TypingPresenceRowsProps,
): JSX.Element | null {
  // The clock runs only while the store holds something; an empty room costs
  // nothing at all.
  const now = useTypingClock((docs?.length ?? 0) > 0);
  const view = composeTypingRows({
    docs: docs ?? [],
    viewerUid: viewerUid ?? '',
    participantUids: participantUids ?? null,
    nowMs: now,
  });
  if (!view.rows.length) return null;
  return (
    <div className="pending-typing-rows" data-typing-rows={String(view.rows.length)}>
      {view.rows.map((row: TypingRow) => {
        const label = labelFor(row.uid);
        return (
          <div
            key={row.uid}
            className="pending-typing-row"
            data-source-tag={TYPING_PRESENCE_SOURCE_TAG}
          >
            <span className="pending-typing-glyph" aria-hidden="true">{label.initial}</span>
            {/* copy: typing_presence — state words only; the draft's text and its
                length are never published, so neither can be rendered. */}
            <span className="pending-typing-label">{`${label.name} is typing…`}</span>
          </div>
        );
      })}
      {view.hidden > 0 ? (
        <div className="pending-typing-more">{`+${Math.floor(view.hidden)} more`}</div>
      ) : null}
    </div>
  );
}
